/**
 * @fileoverview Centralised Error Types & Retry Policy
 * @description Unified error contract for all packages. Replaces the 5 different
 * ad-hoc error patterns previously spread across cortex-protocol, base-service,
 * service.ts, service-registry, and forms.
 * @module @flowstate/types/errors
 */

import { z } from 'zod';

// ============================================================================
// ERROR CODES
// ============================================================================

/**
 * Machine-readable error codes shared across all services.
 * Extend this list — do NOT define local error code strings in individual packages.
 */
export const ErrorCodeSchema = z.enum([
  // ── HTTP-level ─────────────────────────────────────────────────────────────
  'INTERNAL_SERVER_ERROR',
  'NOT_FOUND',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'VALIDATION_ERROR',
  'CONFLICT',
  'BAD_REQUEST',
  'UNPROCESSABLE_ENTITY',

  // ── Network / infrastructure ───────────────────────────────────────────────
  'RATE_LIMITED',
  'TIMEOUT',
  'SERVICE_UNAVAILABLE',
  'NETWORK_ERROR',

  // ── Auth ───────────────────────────────────────────────────────────────────
  'SESSION_EXPIRED',
  'INVALID_TOKEN',
  'INSUFFICIENT_PERMISSIONS',
  'MFA_REQUIRED',

  // ── Cortex / AI ────────────────────────────────────────────────────────────
  'CORTEX_AUTH_INVALID',
  'CORTEX_SESSION_LOCKED',
  'CORTEX_BUDGET_EXCEEDED',
  'CORTEX_MODEL_UNAVAILABLE',
  'CORTEX_TOOL_FAILED',
  'CORTEX_CONTEXT_OVERFLOW',
  'CORTEX_STREAM_INTERRUPTED',

  // ── Booking ────────────────────────────────────────────────────────────────
  'BOOKING_CONFLICT',
  'BOOKING_UNAVAILABLE',
  'BOOKING_REQUIRES_APPROVAL',
  'BOOKING_OUTSIDE_BUSINESS_HOURS',
  'BOOKING_SLOT_TAKEN',

  // ── Billing / Stripe ───────────────────────────────────────────────────────
  'PAYMENT_FAILED',
  'SUBSCRIPTION_INACTIVE',
  'TIER_LIMIT_EXCEEDED',
  'FEATURE_NOT_AVAILABLE',

  // ── Storage / files ────────────────────────────────────────────────────────
  'FILE_TOO_LARGE',
  'UNSUPPORTED_FILE_TYPE',
  'STORAGE_QUOTA_EXCEEDED',

  // ── Integration ────────────────────────────────────────────────────────────
  'INTEGRATION_AUTH_FAILED',
  'INTEGRATION_SYNC_FAILED',
  'INTEGRATION_NOT_CONNECTED',
]);

export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

// ============================================================================
// SERVICE ERROR
// ============================================================================

/**
 * Typed error object returned from any service layer call.
 * Use this instead of raw `Error` or ad-hoc `{ error: string }` shapes.
 * NOTE: This is the richer, code-aware variant. The legacy `ServiceError` from
 * `./service` still exists for backwards compatibility; prefer this type for
 * new code and migrations.
 *
 * @example
 * const err: TypedServiceError = {
 *   code: 'NOT_FOUND',
 *   message: 'Client not found',
 *   statusCode: 404,
 *   retryable: false,
 * };
 */
export interface TypedServiceError<TContext = unknown> {
  /** Machine-readable error code */
  code: ErrorCode;
  /** Human-readable description */
  message: string;
  /** HTTP-equivalent status code for edge function / API route responses */
  statusCode: number;
  /** Whether the caller should attempt a retry */
  retryable: boolean;
  /** Milliseconds to wait before retrying (set when server returns Retry-After) */
  retryAfterMs?: number;
  /** Optional diagnostic context (not shown to end users in production) */
  context?: TContext;
  /** Original underlying error for logging/tracing */
  cause?: unknown;
}

// ============================================================================
// RETRY POLICY
// ============================================================================

export const RetryPolicySchema = z.object({
  /** Maximum number of retry attempts (0 = no retry) */
  maxRetries: z.number().int().min(0).max(10).default(3),
  /** Base delay in milliseconds before the first retry */
  baseDelayMs: z.number().int().min(50).max(30_000).default(500),
  /** Multiplier applied to delay for each subsequent attempt (exponential back-off) */
  backoffMultiplier: z.number().min(1).max(10).default(2),
  /** Whether to add random jitter to avoid thundering-herd */
  jitter: z.boolean().default(true),
  /** Only retry on these error codes (empty = retry on all retryable errors) */
  retryOn: z.array(ErrorCodeSchema).optional(),
});

export type RetryPolicy = z.infer<typeof RetryPolicySchema>;

/** Sensible default retry policy for most service calls. */
export const DEFAULT_RETRY_POLICY: RetryPolicy = {
  maxRetries: 3,
  baseDelayMs: 500,
  backoffMultiplier: 2,
  jitter: true,
};

/** More aggressive policy for critical paths (e.g. payment webhooks). */
export const CRITICAL_RETRY_POLICY: RetryPolicy = {
  maxRetries: 5,
  baseDelayMs: 1_000,
  backoffMultiplier: 1.5,
  jitter: true,
};

/** No-retry policy — for idempotent operations where duplicates are harmful. */
export const NO_RETRY_POLICY: RetryPolicy = {
  maxRetries: 0,
  baseDelayMs: 0,
  backoffMultiplier: 1,
  jitter: false,
};

// ============================================================================
// HTTP STATUS HELPERS
// ============================================================================

/** Maps common error codes to canonical HTTP status codes. */
export const ERROR_CODE_STATUS_MAP: Record<ErrorCode, number> = {
  INTERNAL_SERVER_ERROR: 500,
  NOT_FOUND: 404,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  VALIDATION_ERROR: 422,
  CONFLICT: 409,
  BAD_REQUEST: 400,
  UNPROCESSABLE_ENTITY: 422,
  RATE_LIMITED: 429,
  TIMEOUT: 408,
  SERVICE_UNAVAILABLE: 503,
  NETWORK_ERROR: 503,
  SESSION_EXPIRED: 401,
  INVALID_TOKEN: 401,
  INSUFFICIENT_PERMISSIONS: 403,
  MFA_REQUIRED: 403,
  CORTEX_AUTH_INVALID: 401,
  CORTEX_SESSION_LOCKED: 409,
  CORTEX_BUDGET_EXCEEDED: 402,
  CORTEX_MODEL_UNAVAILABLE: 503,
  CORTEX_TOOL_FAILED: 500,
  CORTEX_CONTEXT_OVERFLOW: 422,
  CORTEX_STREAM_INTERRUPTED: 500,
  BOOKING_CONFLICT: 409,
  BOOKING_UNAVAILABLE: 404,
  BOOKING_REQUIRES_APPROVAL: 202,
  BOOKING_OUTSIDE_BUSINESS_HOURS: 422,
  BOOKING_SLOT_TAKEN: 409,
  PAYMENT_FAILED: 402,
  SUBSCRIPTION_INACTIVE: 402,
  TIER_LIMIT_EXCEEDED: 402,
  FEATURE_NOT_AVAILABLE: 402,
  FILE_TOO_LARGE: 413,
  UNSUPPORTED_FILE_TYPE: 415,
  STORAGE_QUOTA_EXCEEDED: 507,
  INTEGRATION_AUTH_FAILED: 401,
  INTEGRATION_SYNC_FAILED: 502,
  INTEGRATION_NOT_CONNECTED: 424,
};

// ============================================================================
// FACTORY HELPERS
// ============================================================================

/** Create a typed TypedServiceError with status code auto-populated. */
export function createServiceError<TContext = unknown>(
  code: ErrorCode,
  message: string,
  options?: {
    retryable?: boolean;
    retryAfterMs?: number;
    context?: TContext;
    cause?: unknown;
  },
): TypedServiceError<TContext> {
  return {
    code,
    message,
    statusCode: ERROR_CODE_STATUS_MAP[code],
    retryable: options?.retryable ?? (code === 'RATE_LIMITED' || code === 'TIMEOUT' || code === 'SERVICE_UNAVAILABLE' || code === 'NETWORK_ERROR'),
    retryAfterMs: options?.retryAfterMs,
    context: options?.context,
    cause: options?.cause,
  };
}
